import { User } from '@mi-front/core';
import { Chip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { QueryView } from '@ui/utils/async-view/QueryView';
import { defineMessage, useIntl } from 'react-intl';
import { fetchUser } from '../../modules/admin/api';
import { userRoleIconMap } from '../../modules/admin/screens/users/components/UserRoleIconMap';
import { UserLabel } from './UserLabel';

const unknownUserMessage = defineMessage({
  id: 'users.chipList.unknownUser',
  defaultMessage: 'Usuario desconocido',
});

export type MissingUserDisplay = 'hide' | 'show';

export interface UserChipProps {
  userId: string;
  missingUserDisplay: MissingUserDisplay;
}

/** Single user chip that fetches user by id and shows label/role; handles 404 via missingUserDisplay. */
export function UserChip({ userId, missingUserDisplay }: UserChipProps) {
  const { $t } = useIntl();

  const userQuery = useQuery({
    queryKey: ['user', userId],
    queryFn: () => fetchUser(userId),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return (
    <QueryView inline query={userQuery} ignore404>
      {(user: User | undefined) => {
        if (user == null) {
          if (missingUserDisplay === 'hide') {
            return <></>;
          }
          return <Chip label={$t(unknownUserMessage)} variant="outlined" />;
        }
        const icon = userRoleIconMap[user.role] || null;
        return (
          <Chip
            icon={icon as React.ReactElement}
            label={<UserLabel id={userId} />}
            variant="outlined"
          />
        );
      }}
    </QueryView>
  );
}
